import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Radio } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 15000;

const SEGMENT_NOUNS = {
  driver: "driver",
  owner: "owner operator",
  carrier: "carrier",
  need_driver: "carrier looking for a driver",
  rv_service: "RV owner",
  rv_dealer: "RV dealer",
  geo_notify: "driver",
};

const formatMessage = (lead) => {
  const noun = SEGMENT_NOUNS[lead.segment] || "person";
  if (lead.state) return `Someone in ${lead.state} just pre-registered as a ${noun}!`;
  return `A new ${noun} just joined the RelayRigs waitlist!`;
};

// Real-time social-proof ticker — only toasts when an ACTUAL new pre-registration
// happens (polls for new lead ids), never replays old signups. No PII is fetched or shown.
export const LiveSignupFeed = () => {
  const seenIdsRef = useRef(null);

  useEffect(() => {
    const showToastFor = (lead) => {
      toast.custom(
        () => (
          <div
            data-testid="live-signup-toast"
            className="flex items-center gap-2.5 bg-white border border-rr-line shadow-lg rounded-full px-4 py-3 text-sm font-semibold text-rr-navy"
          >
            <Radio size={15} className="text-rr-orange shrink-0" />
            {formatMessage(lead)}
          </div>
        ),
        { duration: 5000 }
      );
    };

    const poll = () => {
      axios
        .get(`${API}/leads/recent?limit=10`)
        .then((res) => {
          const leads = res.data?.leads || [];
          if (seenIdsRef.current === null) {
            // Baseline snapshot on first load — don't toast for pre-existing signups.
            seenIdsRef.current = new Set(leads.map((l) => l.id));
            return;
          }
          const newLeads = leads.filter((l) => l.id && !seenIdsRef.current.has(l.id));
          if (newLeads.length > 0) {
            // Oldest-first so toasts appear in the order they actually happened.
            [...newLeads].reverse().forEach(showToastFor);
            newLeads.forEach((l) => seenIdsRef.current.add(l.id));
          }
        })
        .catch(() => {});
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return null;
};
